/**
 * Dialog «Scarica dati aggiuntivi» (SPEC-016 §3.3): la richiesta ACCODA un
 * task, non esegue — il dialog lo dice, insieme alla quota. Riusa il `Modal`
 * di `ui/index.tsx` (F-040).
 */

import { useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { Alert, Button, Field, Modal } from '@/ui';
import { useRequestBackfill } from './api';

type Props = {
  instrumentId: string;
  onClose: () => void;
};

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 24 * 3600 * 1000).toISOString().slice(0, 10);
}

export function BackfillDialog({ instrumentId, onClose }: Props) {
  const [from, setFrom] = useState(isoDaysAgo(365));
  const [to, setTo] = useState(isoDaysAgo(0));
  const backfill = useRequestBackfill(instrumentId);

  const submit = () => backfill.mutate({ from, to });

  const errorText = (() => {
    if (!backfill.isError) return null;
    const error = backfill.error;
    if (error instanceof ApiError && error.status === 429) {
      return 'Quota giornaliera di download esaurita: la richiesta è stata rifiutata, non troncata. Riprova domani.';
    }
    return error instanceof ApiError ? error.message : 'Errore imprevisto';
  })();

  return (
    <Modal title="Scarica dati aggiuntivi" onClose={onClose}>
      <p>
        Il download viene <strong>accodato</strong> (provider Yahoo Finance, granularità
        giornaliera) e conta sulla quota giornaliera di backfill. Le righe già presenti non vengono
        mai sostituite: fonti diverse convivono sulla stessa seduta.
      </p>
      <div className="backfill__fields">
        <Field label="Dal" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        <Field label="Al" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
      </div>

      {errorText && (
        <Alert variant="error" title="Richiesta rifiutata">
          {errorText}
        </Alert>
      )}
      {backfill.isSuccess && backfill.data.deduplicated && (
        <Alert variant="info" title="Già in coda">
          Un download identico (stesso intervallo, stesso provider) è già in attesa: non ne è stato
          accodato un altro.
        </Alert>
      )}
      {backfill.isSuccess && !backfill.data.deduplicated && (
        <Alert variant="info" title="Download accodato">
          Il task è in coda: i dati compariranno sul grafico entro qualche minuto, con la loro fonte
          dichiarata. Puoi chiudere questa finestra.
        </Alert>
      )}

      <div className="modal-actions">
        <Button variant="secondary" onClick={onClose}>
          Chiudi
        </Button>
        <Button onClick={submit} loading={backfill.isPending} disabled={!from || !to || from > to}>
          Accoda il download
        </Button>
      </div>
    </Modal>
  );
}
